import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend } from "recharts";
import { Boxes, CheckCircle2, Lock, IndianRupee, PackagePlus, Scissors, PackageMinus, Truck, Disc, CircleDashed, Clock, AlertTriangle, Sparkles, FileText, Search } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ScanButton } from "@/components/QrScanner";
import { api, fmtQty, fmtINR, fmtDT, TXN_LABELS } from "@/lib/api";
import { PageHeader, StatCard, Empty, tableWrap, th, td, num } from "@/components/common";

const otherUnits = (obj, key) =>
  Object.entries(obj || {}).filter(([u]) => u !== "m").map(([u, v]) => fmtQty(v[key] ?? v.qty, u)).join(" · ");

function ChartCard({ title, children, testid }) {
  return (
    <div className="rounded-lg border bg-card p-4" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-3">{title}</div>
      <div className="h-64">{children}</div>
    </div>
  );
}

const axis = { fontSize: 11, fill: "hsl(var(--muted-foreground))" };

function QuickActions() {
  const { can } = useAuth();
  const nav = useNavigate();
  const acts = [
    ["Cut Cable", "/cutting", Scissors, "cutting:create", true],
    ["Receive Stock", "/inward/new", PackagePlus, "inward:create"],
    ["Dispatch", "/dispatch/new", Truck, "dispatch:create"],
    ["Check Stock", "/live-stock", Search],
    ["Quotation", "/quotations/new", FileText, "quote:write"],
    ["Ask AI", "/ask", Sparkles],
  ].filter((a) => !a[3] || can(a[3]));
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-7 gap-3" data-testid="quick-actions">
      {acts.map(([l, to, I, , main]) => (
        <Link key={to} to={to} data-testid={`quick-${to.replace(/\//g, "-").slice(1)}`}
          className={`group rounded-lg border p-4 flex items-center gap-3 transition-[transform,background-color] hover:-translate-y-0.5 ${main ? "bg-red-600 text-white border-red-600 hover:bg-red-700" : "bg-card hover:bg-muted"}`}>
          <I className="h-6 w-6 shrink-0" /><span className="font-semibold">{l}</span>
        </Link>
      ))}
      <ScanButton label="Scan Drum" testid="quick-scan-drum" className="h-auto p-4 justify-start gap-2 text-base font-semibold" onDrum={(d) => nav(`/drums/${d.id}`)} />
    </div>
  );
}

function AlertStrip({ low }) {
  const [i, setI] = useState(null);
  useEffect(() => { api.get("/insights").then((r) => setI(r.data)).catch(() => {}); }, []);
  const items = [
    low > 0 && `${low} size(s) below minimum stock`,
    i?.reorder.length > 0 && `${i.reorder.length} size(s) to reorder soon`,
    i?.pending_bill.length > 0 && `${i.pending_bill.length} cutting(s) on pending bill`,
    i?.old_pieces > 0 && `${i.old_pieces} cut piece(s) waiting over 7 days`,
  ].filter(Boolean);
  if (!items.length) return null;
  return (
    <Link to="/insights" className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-700 dark:text-red-400 hover:bg-red-500/15 transition-colors" data-testid="dashboard-alert-strip">
      <AlertTriangle className="h-4 w-4" />{items.map((t) => <span key={t} className="font-medium">{t}</span>)}<span className="ml-auto underline">See Smart Alerts</span>
    </Link>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)); }, []);
  if (!d) return <div className="text-sm text-muted-foreground" data-testid="dashboard-loading">Loading live inventory…</div>;
  const m = d.units.m || { physical: 0, reserved: 0, available: 0 };
  const t = d.today;
  const cards = [
    ["Total Physical Stock", fmtQty(m.physical), otherUnits(d.units, "physical"), Boxes, "physical"],
    ["Available Stock", fmtQty(m.available), otherUnits(d.units, "available"), CheckCircle2, "available"],
    ["Reserved Stock", fmtQty(m.reserved), otherUnits(d.units, "reserved"), Lock, "reserved"],
    ["Total Inventory Value", d.inventory_value == null ? "Restricted" : fmtINR(d.inventory_value), "At purchase cost", IndianRupee, "value"],
    ["Today's Stock Inward", fmtQty(t.inward.m?.qty), `${t.inward.m?.count || 0} drums ${otherUnits(t.inward, "qty")}`, PackagePlus, "today-inward"],
    ["Today's Cable Cutting", fmtQty(t.cutting.m?.qty), `${t.cutting.m?.count || 0} cutting entries`, Scissors, "today-cutting"],
    ["Today's Stock Outward", fmtQty(t.outward.m?.qty), otherUnits(t.outward, "qty"), PackageMinus, "today-outward"],
    ["Today's Dispatch", `${t.dispatch_count}`, "Dispatch entries", Truck, "today-dispatch"],
    ["Available Cable Drums", `${d.drums_available}`, "With balance > 0", Disc, "drums-available"],
    ["Partially Used Drums", `${d.drums_partial}`, "Cut at least once", CircleDashed, "drums-partial"],
    ["Pending Dispatches", `${d.pending_dispatches}`, "Cut / reserved, not dispatched", Clock, "pending-dispatch"],
    ["Low-Stock Alerts", `${d.low_stock.length}`, "Below minimum level", AlertTriangle, "low-stock", d.low_stock.length > 0],
  ];
  return (
    <div className="space-y-6">
      <PageHeader title="Home" subtitle="Tap what you want to do. Live figures below are computed from posted stock transactions." testid="dashboard-header" />
      <QuickActions />
      <AlertStrip low={d.low_stock.length} />
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
        {cards.map(([l, v, s, I, id, acc]) => <StatCard key={id} label={l} value={v} sub={s} icon={I} accent={acc} testid={`stat-${id}`} />)}
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <ChartCard title="Brand-wise stock (metres)" testid="chart-brand">
          {d.charts.brand.length ? (
            <ResponsiveContainer><BarChart data={d.charts.brand}><CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" /><XAxis dataKey="name" tick={axis} /><YAxis tick={axis} /><Tooltip /><Bar dataKey="metres" fill="#1E3A8A" radius={[3, 3, 0, 0]} /></BarChart></ResponsiveContainer>
          ) : <Empty text="No stock posted yet" />}
        </ChartCard>
        <ChartCard title="Cable size-wise stock (metres)" testid="chart-size">
          {d.charts.size.length ? (
            <ResponsiveContainer><BarChart data={d.charts.size} layout="vertical"><CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" /><XAxis type="number" tick={axis} /><YAxis type="category" dataKey="name" tick={axis} width={110} /><Tooltip /><Bar dataKey="metres" fill="#334155" radius={[0, 3, 3, 0]} /></BarChart></ResponsiveContainer>
          ) : <Empty text="No stock posted yet" />}
        </ChartCard>
        <ChartCard title="Monthly inward vs outward (metres)" testid="chart-monthly">
          {d.charts.monthly.length ? (
            <ResponsiveContainer><BarChart data={d.charts.monthly}><CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" /><XAxis dataKey="month" tick={axis} /><YAxis tick={axis} /><Tooltip /><Legend /><Bar dataKey="inward" fill="#1E3A8A" /><Bar dataKey="outward" fill="#DC2626" /></BarChart></ResponsiveContainer>
          ) : <Empty text="No movements yet" />}
        </ChartCard>
        <ChartCard title="Daily cutting — last 14 days (metres)" testid="chart-cutting">
          {d.charts.daily_cutting.length ? (
            <ResponsiveContainer><BarChart data={d.charts.daily_cutting}><CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" /><XAxis dataKey="date" tick={axis} /><YAxis tick={axis} /><Tooltip /><Bar dataKey="metres" fill="#DC2626" radius={[3, 3, 0, 0]} /></BarChart></ResponsiveContainer>
          ) : <Empty text="No cutting yet" />}
        </ChartCard>
        <ChartCard title="Most frequently cut sizes (this month)" testid="chart-top-sizes">
          {d.charts.top_cut_sizes.length ? (
            <ResponsiveContainer><BarChart data={d.charts.top_cut_sizes} layout="vertical"><CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" /><XAxis type="number" tick={axis} allowDecimals={false} /><YAxis type="category" dataKey="name" tick={axis} width={150} /><Tooltip /><Bar dataKey="count" name="cuts" fill="#1E3A8A" /></BarChart></ResponsiveContainer>
          ) : <Empty text="No cutting yet" />}
        </ChartCard>
        <ChartCard title="Employee-wise cutting (this month, metres)" testid="chart-employee">
          {d.charts.employee_cutting.length ? (
            <ResponsiveContainer><BarChart data={d.charts.employee_cutting}><CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" /><XAxis dataKey="name" tick={axis} /><YAxis tick={axis} /><Tooltip /><Bar dataKey="metres" fill="#334155" /></BarChart></ResponsiveContainer>
          ) : <Empty text="No cutting yet" />}
        </ChartCard>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-2">Low-stock alerts</div>
          <div className={tableWrap}>
            <table className="w-full" data-testid="low-stock-table">
              <thead><tr><th className={th}>SKU</th><th className={th}>Brand</th><th className={th}>Size</th><th className={`${th} text-right`}>Available</th><th className={`${th} text-right`}>Min</th></tr></thead>
              <tbody>{d.low_stock.map((r) => (
                <tr key={r.sku}><td className={`${td} font-mono`}>{r.sku}</td><td className={td}>{r.brand}</td><td className={td}>{r.cable_size}</td><td className={`${td} ${num} text-red-600`}>{fmtQty(r.available, r.uom)}</td><td className={`${td} ${num}`}>{fmtQty(r.min_stock, r.uom)}</td></tr>
              ))}</tbody>
            </table>
            {!d.low_stock.length && <Empty text="No low-stock alerts" />}
          </div>
        </div>
        <div>
          <div className="flex justify-between mb-2"><span className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">Recent ledger movements</span><Link to="/ledger" className="text-xs underline" data-testid="dashboard-ledger-link">View ledger</Link></div>
          <div className={tableWrap}>
            <table className="w-full" data-testid="recent-movements-table">
              <thead><tr><th className={th}>Time</th><th className={th}>Type</th><th className={th}>Drum</th><th className={`${th} text-right`}>Qty</th></tr></thead>
              <tbody>{d.recent.map((l) => (
                <tr key={l.id}><td className={td}>{fmtDT(l.at)}</td><td className={td}>{TXN_LABELS[l.txn_type]}</td><td className={`${td} font-mono`}>{l.drum_code}</td><td className={`${td} ${num}`}>{fmtQty(l.qty, l.uom)}</td></tr>
              ))}</tbody>
            </table>
            {!d.recent.length && <Empty text="No movements yet" />}
          </div>
        </div>
      </div>
    </div>
  );
}
